import React from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import Flow from 'flow';
import { createSession } from 'flow/actions/chat-actions';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import MailToWrapper from 'flow/components/custom-components/MailToWrapper';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import VideoCallButton from 'flow/components/VideoCallButton';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectChatSessionByChatUser } from 'flow/selectors/chat-selectors';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapabilityVideo,
    selectOwnUserHasCapabilityVideo1on1,
} from 'flow/selectors/ExtensionSelectors';
import { addChatLogEntry } from 'flow/utils/browser-utils';
import { getCallableNumberFromContact } from 'flow/utils/extension-utils';
import { addMeetingLog } from 'flow/utils/meeting-utils';
import { twMerge } from 'tailwind-merge';

export const SquareButtonsContainer = (props) => {
    const {
        handleTogglePresenceNotification,
        shouldNotifyPresence,
        allowPresenceNotification,
        emailAdress,
        contact,
        extensionKey,
        className,
        disableVideoCall = false,
        disableMessaging = false,
    } = props;
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const ownUserHasVideoCapability = useAppSelector(
        selectOwnUserHasCapabilityVideo,
    );
    const ownUserHasVideo1on1Capability = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const chatUserKey = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey, 'chatUserKey']),
    );
    const chatSessionKey = useAppSelector((state) =>
        selectChatSessionByChatUser(state, chatUserKey, ownExtensionKey),
    );

    const { hasFeature } = useFeatureFlags();

    const navigateToLobby = async () => {
        if (chatSessionKey) {
            history.push(
                generatePath(routes['messages.id.lobby'], {
                    id: chatSessionKey,
                }),
            );
        } else {
            try {
                const newChatSessionKey = await dispatch(
                    createSession(chatUserKey),
                );
                history.push(
                    generatePath(routes['messages.id.lobby'], {
                        id: newChatSessionKey,
                    }),
                );
            } catch (error) {
                addMeetingLog(
                    'Failed to create chat session for meeting lobby redirect',
                    error,
                );
            }
        }
    };

    const navigateToChat = async () => {
        if (chatSessionKey) {
            history.push(
                generatePath(routes['messages.id'], { id: chatSessionKey }),
            );
        } else {
            try {
                const newChatSessionKey = await dispatch(
                    createSession(chatUserKey),
                );
                history.push(
                    generatePath(routes['messages.id'], {
                        id: newChatSessionKey,
                    }),
                );
            } catch (error) {
                addChatLogEntry('Failed to create chat session', error);
            }
        }
    };

    const handleChatInteraction = async (ev) => {
        ev.stopPropagation();
        await navigateToChat();
    };

    const handleMeetingInteraction = async (ev) => {
        ev.stopPropagation();
        await navigateToLobby();
    };

    const VideoButton = () => {
        if (
            !chatUserKey ||
            disableVideoCall ||
            (!ownUserHasVideoCapability && !ownUserHasVideo1on1Capability)
        ) {
            return null;
        }

        if (
            hasFeature(FeatureFlags.VIDEO_CANVAS) &&
            !!extensionKey &&
            ownUserHasVideo1on1Capability
        ) {
            return (
                <VideoCallButton
                    extensionKey={extensionKey}
                    chatSessionKey={chatSessionKey}
                />
            );
        }

        return (
            <IconButton
                className="size-10 rounded-sm bg-gray-200"
                onClick={handleMeetingInteraction}
                tooltipLabel={i18next.t('Tooltip.Title.Go_to_lobby')}
            >
                <Icon iconKey="video" />
            </IconButton>
        );
    };

    const callButtonProps = {
        phoneNumber: getCallableNumberFromContact(contact),
        extensionKey: extensionKey,
        transferContact: contact,
    };

    return (
        <div className={twMerge('flex shrink-0 gap-x-2', className)}>
            {allowPresenceNotification ? (
                <IconButton
                    className="size-10 rounded-sm bg-gray-200"
                    onClick={handleTogglePresenceNotification}
                    tooltipLabel={i18next.t(
                        'General.ExtensionBusyAlet.NotifyWhenAvailable',
                    )}
                >
                    {shouldNotifyPresence ? (
                        <Icon iconKey="notifications" />
                    ) : (
                        <Icon iconKey="notificationsNoneIcon" />
                    )}
                </IconButton>
            ) : null}

            {emailAdress && (
                <MailToWrapper recipient={emailAdress}>
                    <IconButton
                        className="size-10 rounded-sm bg-gray-200"
                        tooltipLabel={i18next.t('Tooltip.Title.SendEmail')}
                    >
                        <Icon iconKey="email" />
                    </IconButton>
                </MailToWrapper>
            )}

            {chatUserKey && !disableMessaging && (
                <IconButton
                    className="size-10 rounded-sm bg-gray-200"
                    onClick={handleChatInteraction}
                    tooltipLabel={i18next.t('Tooltip.Title.SendMessage')}
                >
                    <Icon iconKey="chat" />
                </IconButton>
            )}

            <VideoButton />

            <CallAdaptiveButton
                {...callButtonProps}
                size="medium"
                activeBg={true}
                bgColor={'bg-gray-200'}
            />
        </div>
    );
};

SquareButtonsContainer.propTypes = {
    contact: PropTypes.instanceOf(Map),
    extensionKey: Flow.PropTypes.extensionKey,
    emailAdress: PropTypes.string,
    handleTogglePresenceNotification: PropTypes.func,
    shouldNotifyPresence: PropTypes.bool,
    allowPresenceNotification: PropTypes.bool,
    chatSessionKey: Flow.PropTypes.chatSessionKey,
    className: PropTypes.string,
    disableVideoCall: PropTypes.bool,
    disableMessaging: PropTypes.bool,
};

export default SquareButtonsContainer;
